import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  apiFetch,
  readDetailMessage,
  setProductStatus,
  uploadVariantImage,
  type Announcement,
} from "@/lib/api";
import { useSetVariantImage, useVariantImagesMap } from "@/lib/variantImages";
import { queryClient } from "@/lib/queryClient";
import { clearProductOrigin } from "@/lib/productNavigation";
import { AnnouncementPicker } from "./AnnouncementPicker";
import { UnavailableToggle } from "./UnavailableToggle";
import { LastVariantDeleteModal } from "./LastVariantDeleteModal";
import {
  VariantFields,
  emptyVariant,
  variantDraftToPayload,
  variantToDraft,
  validateVariantDraft,
  PENDING_IMAGE_PLACEHOLDER,
  type VariantDraft,
} from "./VariantFields";

export type Variant = {
  id: number;
  sku: string;
  color: string | null;
  color_en: string | null;
  flavour: string | null;
  flavour_en: string | null;
  size: string | null;
  size_en: string | null;
  img_url: string;
  price: number;
  announcement_id?: string | null;
  announcement?: Announcement | null;
  is_unavailable?: boolean;
};

type VariantsManagerProps = {
  productId: string;
  productStatus: string;
  variants: Variant[];
  onProductDeleted?: () => void;
  onProductStatusChange?: (status: "draft") => void;
  onVariantsChange?: (count: number) => void;
  onExposeAddPendingVariant?: (trigger: () => Promise<number | null>) => void;
  announcementIds: Record<number, string | null>;
  onAnnouncementChange: (variantId: number, announcementId: string | null) => void;
  unavailableIds: Record<number, boolean>;
  onUnavailableChange: (variantId: number, unavailable: boolean) => void;
  onVariantDeleted: (variantId: number) => void;
};

export function VariantsManager({
  productId,
  productStatus,
  variants,
  onProductDeleted,
  onProductStatusChange,
  onVariantsChange,
  onExposeAddPendingVariant,
  announcementIds,
  onAnnouncementChange,
  unavailableIds,
  onUnavailableChange,
  onVariantDeleted,
}: VariantsManagerProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Variant[]>(variants);

  useEffect(() => {
    onVariantsChange?.(items.length);
  }, [items]);
  const [drafts, setDrafts] = useState<Record<number, VariantDraft>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [lastVariantToDelete, setLastVariantToDelete] = useState<Variant | null>(null);
  const [lastVariantAction, setLastVariantAction] = useState<"draft" | "product" | null>(null);
  const [lastVariantError, setLastVariantError] = useState<string | null>(null);
  const [variantActionNudges, setVariantActionNudges] = useState<Record<number, number>>({});
  const [newVariantActionNudge, setNewVariantActionNudge] = useState(0);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [newVariant, setNewVariant] = useState<VariantDraft>(emptyVariant);
  const [newError, setNewError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAnnouncementId, setNewAnnouncementId] = useState<string | null>(null);
  const [newUnavailable, setNewUnavailable] = useState(false);
  const setVariantImage = useSetVariantImage();

  // Shared with the public pages via react-query - if this product's
  // pictures were already resolved elsewhere, they show up here with no
  // extra request.
  const itemIds = useMemo(() => items.map(v => v.id), [items]);
  const imageUrls = useVariantImagesMap(itemIds);

  const draftFor = (variant: Variant): VariantDraft =>
    drafts[variant.id] ?? variantToDraft(variant, imageUrls[variant.id] ?? null);

  const setDraft = (id: number, draft: VariantDraft) => {
    setDrafts(prev => ({ ...prev, [id]: draft }));
    setVariantActionNudges(prev => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));
  };

  const nudgeVariantAction = (id: number) => {
    setVariantActionNudges(prev => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));
  };

  const localizeVariantError = (error: unknown, fallback: string): string => {
    const message = error instanceof Error ? error.message : fallback;
    const unsupportedFormat = /^Unsupported image format:\s*(.+)$/i.exec(message);
    return unsupportedFormat
      ? t("variant.unsupportedImageFormat", { format: unsupportedFormat[1] })
      : message;
  };

  const saveVariant = async (variant: Variant) => {
    const draft = draftFor(variant);
    // undefined = the signed URL is still resolving, not "no image" - don't
    // block a save on a picture we simply haven't finished loading.
    const imageResolving = !draft.imageFile && imageUrls[variant.id] === undefined;
    const validationError = validateVariantDraft(
      imageResolving ? { ...draft, existingImageUrl: draft.existingImageUrl ?? "loading" } : draft
    );
    if (validationError) {
      setErrors(prev => ({ ...prev, [variant.id]: t(`variant.${validationError}`) }));
      return;
    }

    setSavingId(variant.id);
    setErrors(prev => ({ ...prev, [variant.id]: "" }));
    try {
      const res = await apiFetch(
        `/variant/${variant.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...variantDraftToPayload(draft),
            announcement_id: announcementIds[variant.id] ?? null,
            is_unavailable: unavailableIds[variant.id] ?? false,
          }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(readDetailMessage(body?.detail, t("variant.couldNotUpdate")));
      }
      const updated: Variant = await res.json();

      // The variant row is already saved at this point, so it's safe to
      // upload its picture now - we never touch Supabase before the variant
      // it belongs to actually exists.
      if (draft.imageFile) {
        const url = await uploadVariantImage(updated.id, draft.imageFile);
        if (url) setVariantImage(updated.id, url);
      }

      setItems(prev => prev.map(v => (v.id === variant.id ? updated : v)));
      onAnnouncementChange(updated.id, updated.announcement_id ?? null);
      onUnavailableChange(updated.id, updated.is_unavailable ?? false);
      clearProductOrigin();
      await queryClient.invalidateQueries({ queryKey: ["products", "announced"] });
      setDrafts(prev => {
        const next = { ...prev };
        delete next[variant.id];
        return next;
      });
      setVariantActionNudges(prev => {
        const next = { ...prev };
        delete next[variant.id];
        return next;
      });
    } catch (err) {
      const message = localizeVariantError(err, t("variant.couldNotUpdate"));
      setErrors(prev => ({ ...prev, [variant.id]: message }));
    } finally {
      setSavingId(null);
    }
  };

  const removeDeletedVariant = async (variant: Variant, keepBlankEditor = false) => {
    setItems(prev => prev.filter(v => v.id !== variant.id));
    onVariantDeleted(variant.id);
    if (keepBlankEditor) {
      setNewVariant(emptyVariant);
      setNewAnnouncementId(null);
      setNewUnavailable(false);
      setNewError(null);
      setNewVariantActionNudge(0);
      setShowAddForm(true);
    }
    clearProductOrigin();
    await queryClient.invalidateQueries({ queryKey: ["products", "announced"] });
  };

  const deleteVariant = async (variant: Variant) => {
    const isLast = items.length === 1;
    if (isLast && productStatus === "published") {
      setLastVariantError(null);
      setLastVariantToDelete(variant);
      return;
    }
    if (!confirm(isLast ? t("variant.deleteLastDraftConfirm") : t("variant.deleteConfirm"))) return;

    setDeletingId(variant.id);
    try {
      const res = await apiFetch(`/variant/${variant.id}`, { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const detail = readDetailMessage(body?.detail, t("variant.couldNotDelete"));
        if (res.status === 409 && detail === "A published product must have at least one variant") {
          setLastVariantError(null);
          setLastVariantToDelete(variant);
          return;
        }
        throw new Error(detail);
      }

      await removeDeletedVariant(variant, isLast);
    } catch (err) {
      const message = err instanceof Error ? err.message : t("variant.couldNotDelete");
      setErrors(prev => ({ ...prev, [variant.id]: message }));
    } finally {
      setDeletingId(null);
    }
  };

  const moveToDraftAndDeleteVariant = async () => {
    if (!lastVariantToDelete) return;
    setLastVariantAction("draft");
    setLastVariantError(null);
    try {
      await setProductStatus(productId, "draft");
      onProductStatusChange?.("draft");

      const res = await apiFetch(`/variant/${lastVariantToDelete.id}`, { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(readDetailMessage(body?.detail, t("variant.couldNotDelete")));
      }

      await removeDeletedVariant(lastVariantToDelete, true);
      setLastVariantToDelete(null);
    } catch (err) {
      setLastVariantError(
        err instanceof Error ? err.message : t("variant.couldNotMoveToDraftAndDelete")
      );
    } finally {
      setLastVariantAction(null);
    }
  };

  const deleteWholeProduct = async () => {
    setLastVariantAction("product");
    setLastVariantError(null);
    try {
      const res = await apiFetch(`/product/${productId}`, { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(readDetailMessage(body?.detail, t("product.couldNotDeleteProduct")));
      }

      clearProductOrigin();
      await queryClient.invalidateQueries({ queryKey: ["products", "announced"] });
      setLastVariantToDelete(null);
      onProductDeleted?.();
    } catch (err) {
      setLastVariantError(
        err instanceof Error ? err.message : t("product.couldNotDeleteProduct")
      );
    } finally {
      setLastVariantAction(null);
    }
  };

  // Returns the resulting variant count, or null if adding failed/was invalid
  // (in which case the error is already shown via newError). A blank draft
  // (nothing typed) is treated as "nothing to add" and resolves to the
  // current count, so callers can use this to flush a pending draft without
  // forcing the user to have touched the "Add a variant" section at all.
  const addPendingVariant = async (): Promise<number | null> => {
    const isBlank =
      !newVariant.sku.trim() &&
      !newVariant.color.trim() &&
      !newVariant.color_en.trim() &&
      !newVariant.flavour.trim() &&
      !newVariant.flavour_en.trim() &&
      !newVariant.size.trim() &&
      !newVariant.size_en.trim() &&
      !newVariant.price.trim() &&
      !newVariant.imageFile;
    if (isBlank) return items.length;

    const validationError = validateVariantDraft(newVariant);
    if (validationError) {
      setNewError(t(`variant.${validationError}`));
      return null;
    }

    setAdding(true);
    setNewError(null);
    try {
      const res = await apiFetch("/variant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...variantDraftToPayload(newVariant),
          img_url: PENDING_IMAGE_PLACEHOLDER,
          product_id: productId,
          announcement_id: newAnnouncementId,
          is_unavailable: newUnavailable,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(readDetailMessage(body?.detail, t("variant.couldNotAdd")));
      }
      const created: Variant = await res.json();

      // The variant is saved first; only now do we try to save its picture.
      if (newVariant.imageFile) {
        const url = await uploadVariantImage(created.id, newVariant.imageFile);
        if (url) setVariantImage(created.id, url);
      }

      setItems(prev => [...prev, created]);
      onAnnouncementChange(created.id, created.announcement_id ?? null);
      onUnavailableChange(created.id, created.is_unavailable ?? false);
      setNewVariant(emptyVariant);
      setNewAnnouncementId(null);
      setNewUnavailable(false);
      setNewVariantActionNudge(0);
      clearProductOrigin();
      await queryClient.invalidateQueries({ queryKey: ["products", "announced"] });
      return items.length + 1;
    } catch (err) {
      setNewError(localizeVariantError(err, t("variant.couldNotAdd")));
      return null;
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => {
    onExposeAddPendingVariant?.(addPendingVariant);
  });

  return (
    <>
      <div className="flex flex-col gap-4 w-full max-w-md bg-app-surface text-foreground border border-app-border rounded-2xl shadow-md p-6">
        <h2 className="text-lg font-bold text-brand">{t("product.variants")}</h2>

      {items.map(variant => (
        <div key={variant.id} className="flex flex-col gap-2 border border-app-border rounded-lg p-3">
          <VariantFields
            value={draftFor(variant)}
            onChange={draft => setDraft(variant.id, draft)}
            variantId={variant.id}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-foreground">
              {t("announcementAssignment.variantLabel")}
            </label>
            <AnnouncementPicker
              value={announcementIds[variant.id] ?? null}
              onChange={announcementId => {
                onAnnouncementChange(variant.id, announcementId);
                nudgeVariantAction(variant.id);
              }}
            />
          </div>
          <UnavailableToggle
            checked={unavailableIds[variant.id] ?? false}
            onChange={unavailable => {
              onUnavailableChange(variant.id, unavailable);
              nudgeVariantAction(variant.id);
            }}
          />
          {errors[variant.id] && <span className="text-red-500 text-sm">{errors[variant.id]}</span>}
          <div className="flex gap-2">
            <button
              key={`save-${variant.id}-${variantActionNudges[variant.id] ?? 0}`}
              type="button"
              className={`rounded-lg px-3 py-1.5 text-sm font-medium text-white bg-brand hover:bg-brand/90 transition-colors duration-200 cursor-pointer disabled:opacity-50 ${
                variantActionNudges[variant.id] ? "variant-action-nudge" : ""
              }`}
              onClick={() => saveVariant(variant)}
              disabled={savingId === variant.id}
            >
              {savingId === variant.id ? t("common.saving") : t("common.save")}
            </button>
            <button
              type="button"
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors duration-200 cursor-pointer disabled:opacity-50"
              onClick={() => deleteVariant(variant)}
              disabled={deletingId === variant.id}
            >
              {deletingId === variant.id ? t("common.deleting") : t("common.delete")}
            </button>
          </div>
        </div>
      ))}

      {showAddForm ? (
        <div className="flex flex-col gap-2 border border-dashed border-app-border rounded-lg p-3">
          <span className="font-medium text-foreground">{t("variant.addVariantSection")}</span>
          <VariantFields
            value={newVariant}
            onChange={draft => {
              setNewVariant(draft);
              setNewVariantActionNudge(value => value + 1);
            }}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-foreground">
              {t("announcementAssignment.variantLabel")}
            </label>
            <AnnouncementPicker
              value={newAnnouncementId}
              onChange={announcementId => {
                setNewAnnouncementId(announcementId);
                setNewVariantActionNudge(value => value + 1);
              }}
            />
          </div>
          <UnavailableToggle
            checked={newUnavailable}
            onChange={unavailable => {
              setNewUnavailable(unavailable);
              setNewVariantActionNudge(value => value + 1);
            }}
          />
          {newError && <span className="text-red-500 text-sm">{newError}</span>}
          <div className="flex gap-2">
            <button
              key={`add-${newVariantActionNudge}`}
              type="button"
              className={`rounded-lg px-3 py-1.5 text-sm font-medium text-white bg-brand hover:bg-brand/90 transition-colors duration-200 cursor-pointer disabled:opacity-50 ${
                newVariantActionNudge ? "variant-action-nudge" : ""
              }`}
              onClick={async () => {
                const result = await addPendingVariant();
                if (result !== null) setShowAddForm(false);
              }}
              disabled={adding}
            >
              {adding ? t("variant.adding") : t("variant.addVariant")}
            </button>
            <button
              type="button"
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-foreground bg-app-control hover:bg-app-control-hover transition-colors duration-200 cursor-pointer disabled:opacity-50"
              onClick={() => {
                setNewVariant(emptyVariant);
                setNewAnnouncementId(null);
                setNewUnavailable(false);
                setNewError(null);
                setNewVariantActionNudge(0);
                setShowAddForm(false);
              }}
              disabled={adding}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="self-start rounded-lg px-3 py-1.5 text-sm font-medium text-brand border border-brand hover:bg-brand/10 transition-colors duration-200 cursor-pointer"
          onClick={() => setShowAddForm(true)}
        >
          {t("product.addAnotherVariant")}
        </button>
      )}
      </div>
      <LastVariantDeleteModal
        open={lastVariantToDelete !== null}
        pendingAction={lastVariantAction}
        error={lastVariantError}
        onClose={() => {
          if (lastVariantAction) return;
          setLastVariantToDelete(null);
          setLastVariantError(null);
        }}
        onMoveToDraftAndDelete={moveToDraftAndDeleteVariant}
        onDeleteProduct={deleteWholeProduct}
      />
    </>
  );
}
